import i18n from "../i18n/config";

type BackendStatus = {
  errorCode?: number;
  status?: string;
  message?: string;
  infoList?: string[];
};

type BackendResponse = {
  timestamp?: string;
  errorCode?: number;
  status?: string;
  reason?: string;
  businessErrorCode?: string;
  errorDetail?: string;
  message?: string;
  localizedMessage?: string;
};

type BackendEnvelope = {
  status?: BackendStatus;
  response?: BackendResponse;
};

const asEnvelope = (raw: any): BackendEnvelope => {
  if (!raw || typeof raw !== "object") return {};

  if (raw.status || raw.response) return raw as BackendEnvelope;
  if (raw.data && (raw.data.status || raw.data.response)) return raw.data as BackendEnvelope;

  return {};
};

const firstTruthy = (...values: Array<any>) => values.find((value) => Boolean(value));

export const parseBackendNotificationDetails = (raw: any) => {
  const envelope = asEnvelope(raw);
  const status = envelope.status || {};
  const response = envelope.response || {};

  const errorCode = firstTruthy(response.errorCode, status.errorCode);
  const reason = firstTruthy(response.reason, response.status, status.status);
  const message = firstTruthy(
    response.localizedMessage,
    response.message,
    response.errorDetail,
    status.message
  );

  const infoParts: string[] = [];

  if (response.businessErrorCode) {
    infoParts.push(`Business Code: ${response.businessErrorCode}`);
  }
  if (errorCode) {
    infoParts.push(`Error Code: ${errorCode}`);
  }
  if (response.timestamp) {
    infoParts.push(`Timestamp: ${response.timestamp}`);
  }
  if (Array.isArray(status.infoList) && status.infoList.length > 0) {
    infoParts.push(`Info: ${status.infoList.join(", ")}`);
  }

  const info = infoParts.join(" | ");
  const businessErrorCode = typeof response.businessErrorCode === "string" ? response.businessErrorCode : undefined;

  return {
    errorCode,
    reason,
    message,
    businessErrorCode,
    info,
  };
};

const RECIPIENT_DOMAIN_CODE = "MAIL_RECIPIENT_DOMAIN_NOT_ALLOWED";
const RECIPIENT_DOMAIN_PREFIX = "Recipient domain is not allowed: ";

export const recipientDomainNotAllowedText = (raw: unknown): string | null => {
  const details = parseBackendNotificationDetails(raw);
  if (details.businessErrorCode !== RECIPIENT_DOMAIN_CODE) return null;
  const backendMessage = String(details.message || "");
  const domains = backendMessage.startsWith(RECIPIENT_DOMAIN_PREFIX)
    ? backendMessage.slice(RECIPIENT_DOMAIN_PREFIX.length)
    : backendMessage;
  return i18n.t("chat.mail.recipientDomainNotAllowed", { domains });
};

